import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MultiSelectContainer,
  SelectLabel,
  SelectTrigger,
  DropdownMenu,
  DropdownOption,
  SelectedTagsContainer,
  SelectedTag,
  PlaceholderText,
  NoOptionsText,
  ErrorMessage,
  ArrowIcon,
} from './PdfMultiSelect.styles';
import { getPdfs } from '../../../services/admin/pdfs';

const PdfMultiSelect = ({ selectedPdfIds = [], onChange, error = '', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Internal state for checkboxes - independent from parent
  const [internalSelection, setInternalSelection] = useState(new Set(selectedPdfIds || []));

  // Sync internal state when parent prop changes (e.g., on edit mode)
  useEffect(() => {
    setInternalSelection(new Set(selectedPdfIds || []));
  }, [selectedPdfIds]);

  // Fetch all PDFs
  const { data: pdfsData } = useQuery({
    queryKey: ['allPdfs'],
    queryFn: () => getPdfs(0, 1000),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const allPdfs = pdfsData?.data ?? [];

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Toggle PDF selection in internal state
  const handleTogglePdf = (pdfId) => {
    const newSelection = new Set(internalSelection);

    if (newSelection.has(pdfId)) {
      newSelection.delete(pdfId);
    } else {
      newSelection.add(pdfId);
    }

    // Update internal state immediately for UI feedback
    setInternalSelection(newSelection);

    // Notify parent with array (not Set)
    onChange(Array.from(newSelection));
  };

  // Remove tag
  const handleRemoveTag = (pdfId) => {
    const newSelection = new Set(internalSelection);
    newSelection.delete(pdfId);

    setInternalSelection(newSelection);
    onChange(Array.from(newSelection));
  };

  // Trigger button click
  const handleTriggerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // Checkbox change handler
  const handleCheckboxChange = (pdfId) => {
    handleTogglePdf(pdfId);
    // Keep dropdown open
  };

  // Get selected PDF names from internal state
  const getSelectedPdfNames = () => {
    return Array.from(internalSelection)
      .map((id) => allPdfs.find((pdf) => pdf._id === id))
      .filter(Boolean)
      .map((pdf) => pdf.pdfName);
  };

  const selectedNames = getSelectedPdfNames();

  // Helper to check if PDF is selected (using internal state)
  const isPdfSelected = (pdfId) => {
    return internalSelection.has(pdfId);
  };

  return (
    <MultiSelectContainer ref={containerRef}>
      <SelectLabel>Select PDFs</SelectLabel>

      {/* Trigger Button */}
      <SelectTrigger
        onClick={handleTriggerClick}
        disabled={disabled}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <div>
          {internalSelection.size > 0 ? (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              {internalSelection.size} PDF(s) selected
            </span>
          ) : (
            <PlaceholderText>Choose PDFs...</PlaceholderText>
          )}
        </div>
        <ArrowIcon isOpen={isOpen}>▼</ArrowIcon>
      </SelectTrigger>

      {/* Dropdown Menu */}
      {isOpen && (
        <DropdownMenu>
          {allPdfs.length > 0 ? (
            allPdfs.map((pdf) => (
              <DropdownOption
                key={pdf._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 12px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  backgroundColor: isPdfSelected(pdf._id) ? '#f0f7ff' : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isPdfSelected(pdf._id)) {
                    e.currentTarget.style.backgroundColor = '#f9f9f9';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isPdfSelected(pdf._id)
                    ? '#f0f7ff'
                    : 'transparent';
                }}
              >
                <input
                  type="checkbox"
                  id={`pdf-${pdf._id}`}
                  checked={isPdfSelected(pdf._id)}
                  onChange={() => handleCheckboxChange(pdf._id)}
                  style={{
                    cursor: 'pointer',
                    marginRight: '10px',
                    accentColor: '#1976d2',
                  }}
                  disabled={disabled}
                />
                <label
                  htmlFor={`pdf-${pdf._id}`}
                  style={{
                    cursor: 'pointer',
                    flex: 1,
                    fontSize: '14px',
                    margin: 0,
                    userSelect: 'none',
                  }}
                >
                  {pdf.pdfName}
                </label>
              </DropdownOption>
            ))
          ) : (
            <NoOptionsText>No PDFs available</NoOptionsText>
          )}
        </DropdownMenu>
      )}

      {/* Selected Tags */}
      {selectedNames.length > 0 && (
        <SelectedTagsContainer>
          {selectedNames.map((name, idx) => (
            <SelectedTag key={Array.from(internalSelection)[idx]}>
              {name}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveTag(Array.from(internalSelection)[idx]);
                }}
                aria-label="Remove PDF"
                style={{
                  cursor: 'pointer',
                  marginLeft: '6px',
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  fontSize: '18px',
                  lineHeight: '1',
                  color: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                ×
              </button>
            </SelectedTag>
          ))}
        </SelectedTagsContainer>
      )}

      {/* Error Message */}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </MultiSelectContainer>
  );
};

export default PdfMultiSelect;
